import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Heading } from '../../atoms/Typography/Heading';
import { Button } from '../../atoms/Button/Button';
import './Dialog.css';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /**
   * 'sheet': a bottom sheet on phones (up to 767 px) and a narrow dialog from
   * 768 px — for short choice lists such as the capture sheet. Default 'default'.
   */
  variant?: 'default' | 'sheet';
  /**
   * Whether the user can dismiss the dialog via the close button, Escape or a
   * backdrop click. Defaults to true. Set to false for dialogs that must only
   * close through an explicit action in their content (e.g. a one-time secret
   * reveal that requires acknowledgement).
   */
  dismissible?: boolean;
  'data-testid'?: string;
}

/**
 * True while another modal opened on top of this one (e.g. a confirmation shown from
 * a detail dialog) holds the focus: that modal owns Tab and Escape then.
 */
function focusIsInOtherModal(dialog: HTMLElement): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || dialog.contains(active)) return false;
  return active.closest('[aria-modal="true"]') !== null;
}

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  variant = 'default',
  dismissible = true,
  'data-testid': testId,
}: DialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open and restore it to the trigger on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [isOpen]);

  // Trap Tab / Shift+Tab inside the dialog (WCAG 2.4.3): focus wraps between the first and
  // last focusable element and never escapes to the page behind the modal.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleTab = (event: KeyboardEvent): void => {
      const dialog = dialogRef.current;
      if (event.key !== 'Tab' || !dialog || focusIsInOtherModal(dialog)) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return (): void => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dismissible) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !(dialogRef.current && focusIsInOtherModal(dialogRef.current))) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, dismissible]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Restore the previous value so closing a dialog stacked on another one keeps
    // the page behind the lower dialog locked.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return (): void => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (dismissible && event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    // Backdrop click is a pointer convenience; Escape is the keyboard equivalent.
    <div className={`dialog-overlay${variant === 'sheet' ? ' dialog-overlay--sheet' : ''}`} role="presentation" onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className={`dialog dialog--${size}${variant === 'sheet' ? ' dialog--sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <div className="dialog__header">
          <Heading level={2} id={titleId} className="dialog__title">
            {title}
          </Heading>
          {dismissible && (
            <Button
              variant="ghost"
              className="dialog__close-btn"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <span aria-hidden="true">×</span>
            </Button>
          )}
        </div>

        <div className="dialog__body">{children}</div>

        {footer != null && <div className="dialog__footer">{footer}</div>}
      </div>
    </div>
  );
}
